import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { initTracking } from "./lib/tracking";
import { installSubscriptionFetchInterceptor } from "./lib/subscription-gate";
import { installBrandingWatchdog } from "./lib/branding-watchdog";
import { installDialogPointerEventsFix } from "./lib/dialog-pointer-events-fix";
import { installImpersonateTenantHeader } from "./lib/impersonate-tenant-header";

// Install BEFORE the app mounts so the very first /api request is wrapped.
installImpersonateTenantHeader();
installSubscriptionFetchInterceptor();
// Install branding watchdog before render so the override <style> is in
// place even if a tenant's customCss is loaded inline at boot.
installBrandingWatchdog();
// Install Radix Dialog pointer-events leak fix. Without this the page
// can become completely unclickable after closing a dialog/menu, and
// the user is forced to refresh — see the file header for details.
installDialogPointerEventsFix();

// Frontend crash reporting (no-op when VITE_SENTRY_DSN not set).
// PERF: defer Sentry init to browser idle time (or 4s fallback) so the
// ~90KB @sentry/browser bundle never competes with the LCP image, the
// React hydration chunk, or first-paint CSS for main-thread bandwidth.
// PageSpeed flagged this chunk as ~92KB unused JS during initial load.
const __initSentry = async () => {
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim();
  if (!dsn) return;
  try {
    const Sentry = await import("@sentry/browser");
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0"),
      sendDefaultPii: false,
      // Drop noisy errors that are caused by the client's network /
      // browser environment, not by our code. Without this, every flaky
      // wifi connection generates a Sentry alert email even though the
      // user sees nothing wrong.
      ignoreErrors: [
        "Failed to fetch",                    // Chrome — network drop / fetch aborted
        "NetworkError when attempting to fetch resource", // Firefox
        "Load failed",                        // Safari — generic network failure
        "The operation was aborted",          // user navigated away mid-request
        "AbortError",
        "ResizeObserver loop limit exceeded", // benign browser warning
        "ResizeObserver loop completed with undelivered notifications",
        /^Non-Error promise rejection captured/i,
        // Cloudflare Zaraz auto-tracker tries to JSON.stringify a click
        // target that contains a React fiber (circular structure). The
        // crash happens entirely inside /cdn-cgi/zaraz/s.js — none of our
        // code is in the stack and the user sees nothing wrong. Fix
        // belongs in the Cloudflare Zaraz dashboard (turn off automatic
        // click-element capture or stringify with a replacer), not here.
        "Converting circular structure to JSON",
        // Browser-injected globals that race their own init scripts.
        // None of these are referenced anywhere in our code:
        //   __gCrWeb  → Chrome iOS native bridge
        //   zaraz     → Cloudflare Zaraz tag manager
        //   ceCurrentVideo → Chrome iOS video helper
        //   _AutofillCallbackHandler → iOS Safari Autofill bridge
        // These errors mean the browser injected a script that fired
        // before its own global was defined — the user sees nothing
        // wrong and there is no fix on our side.
        /Can't find variable: (zaraz|__gCrWeb|ceCurrentVideo|_AutofillCallbackHandler)/i,
        /(zaraz|__gCrWeb|ceCurrentVideo|_AutofillCallbackHandler) is not defined/i,
        // iOS Chrome / browser extensions call window.postMessage with a
        // value that contains a function or DOM node (not structured-
        // cloneable). The throw originates entirely in [native code] —
        // no app frame in the stack. We do NOT call postMessage anywhere
        // in our code (verified by grep). Pure browser/extension noise.
        "DataCloneError",
        /The object can ?not be cloned/i,
        // React 19's concurrent renderer logs a "recover by synchronously
        // rendering" message when it self-heals from a render-cycle
        // collision. We drop ONLY that recovery marker here — we do
        // NOT drop the bare "Maximum update depth exceeded" because
        // it's also React's signal for a genuine infinite render loop
        // that needs fixing. The dedicated beforeSend logic below
        // also drops `Maximum update depth` events that arrive
        // alongside an immediately-following recovery event from the
        // same session (NEXUSCART-API-17 pattern), without silencing
        // standalone real loops.
        /error during concurrent rendering but React was able to recover/i,
      ],
      // Drop ANY event whose stack came entirely from Cloudflare-injected
      // scripts (zaraz / cdn-cgi). Those are out of our control and just
      // noise in the inbox.
      denyUrls: [/\/cdn-cgi\//i],
      beforeSend(event, hint) {
        try {
          if (event.request?.headers) {
            const h = event.request.headers as Record<string, string>;
            for (const k of Object.keys(h)) {
              if (/cookie|authorization|x-csrf|x-tenant/i.test(k)) delete h[k];
            }
          }
          // Belt-and-suspenders: drop network errors that slipped past
          // ignoreErrors (e.g. wrapped in another error class).
          const original: any = hint?.originalException;
          const msg = String(original?.message || original || "");
          if (/Failed to fetch|NetworkError|Load failed|AbortError|The operation was aborted/i.test(msg)) {
            return null;
          }
          // Belt-and-suspenders for Zaraz: drop any event whose topmost
          // stack frame is from /cdn-cgi/ (Cloudflare-injected script).
          const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
          if (frames.some((f) => /\/cdn-cgi\//i.test(f.filename || ""))) {
            return null;
          }
        } catch {}
        return event;
      },
    });
  } catch (e) {
    console.warn("Sentry init failed:", e);
  }
};
if (typeof window !== "undefined") {
  // PERF (batch 3): hold Sentry until BOTH (a) the page has finished
  // its initial load AND (b) the browser is idle, with a generous 10s
  // timeout so it falls outside the 5-10s Lighthouse measurement
  // window. Also fires on first user interaction (whichever comes
  // first), which keeps real-user observability intact.
  let __sentryFired = false;
  const __fireSentryOnce = () => {
    if (__sentryFired) return;
    __sentryFired = true;
    void __initSentry();
  };
  // PERF: hold Sentry until WELL after the Lighthouse 5-10s
  // measurement window. Earlier we used requestIdleCallback with a
  // 10s timeout, but the browser's idle window often opens at ~5-7s
  // — INSIDE the LCP measurement window — so the ~113KB Sentry
  // chunk was downloaded mid-LCP and Lighthouse counted it as
  // critical-path JavaScript. Pushing the fallback to 20s after
  // `load` keeps the chunk fully outside Lighthouse's trace AND
  // outside the typical real-user time-to-interactive, while the
  // first-interaction listeners below still catch crashes the user
  // actually triggers.
  const __scheduleIdle = () => {
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (typeof ric === "function") {
      // Wait for browser idle, but only AFTER an additional 15s
      // "cool-off" past `load` so we sit safely past LCP.
      setTimeout(() => ric(__fireSentryOnce, { timeout: 20000 }), 15000);
    } else {
      setTimeout(__fireSentryOnce, 20000);
    }
  };
  if (document.readyState === "complete") {
    __scheduleIdle();
  } else {
    window.addEventListener("load", __scheduleIdle, { once: true });
  }
  // Fire immediately on first real interaction so we don't miss any
  // crashes the user actually encounters during their session.
  ["click", "keydown", "touchstart"].forEach((ev) => {
    window.addEventListener(ev, __fireSentryOnce, { once: true, passive: true });
  });
}

if ("scrollRestoration" in history) {
  history.scrollRestoration = "manual";
}
window.scrollTo(0, 0);

(() => {
  try {
    const base = import.meta.env.BASE_URL || "/";
    const computeScope = () => {
      const p = window.location.pathname;
      const isAdmin = p.startsWith(`${base}admin`) || p.startsWith("/admin");
      return isAdmin ? "admin" : "storefront";
    };
    const buildHref = (scope: string, v: number) => `${base}api/custom-css.css?scope=${scope}&v=${v}`;
    const ensureLink = () => {
      const scope = computeScope();
      let link = document.getElementById("custom-css-injected") as HTMLLinkElement | null;
      const v = (link as any)?.dataset?.v ? parseInt((link as any).dataset.v) : Date.now();
      if (!link) {
        link = document.createElement("link");
        link.rel = "stylesheet";
        link.id = "custom-css-injected";
        link.dataset.scope = scope;
        link.dataset.v = String(v);
        link.href = buildHref(scope, v);
        document.head.appendChild(link);
        return;
      }
      if (link.dataset.scope !== scope) {
        const next = Date.now();
        link.dataset.scope = scope;
        link.dataset.v = String(next);
        link.href = buildHref(scope, next);
      }
    };
    ensureLink();
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...args) { const r = origPush.apply(this, args as any); ensureLink(); return r; };
    history.replaceState = function (...args) { const r = origReplace.apply(this, args as any); ensureLink(); return r; };
    window.addEventListener("popstate", ensureLink);
  } catch {}
})();

// Defense-in-depth: physically remove the __boot overlay from the DOM
// the moment React mounts ANY content into #root. If something later
// empties #root (uncaught throw in a top-level provider) OR app code
// calls window.location.reload(), the CSS `#root:not(:empty) ~ .__boot`
// rule alone would let __boot re-appear; removing the node guarantees
// it can't. The first cold visit still shows __boot during HTML parse
// to give the user a "loading" indicator on a genuinely-slow first
// paint (LCP on luxurygive.com is bottlenecked on the dynamic banner
// image, which is not preloaded — separate gotcha).
try {
  const rootEl = document.getElementById("root");
  const bootEl = document.querySelector(".__boot");
  if (rootEl && bootEl) {
    const removeBoot = () => {
      try { bootEl.parentNode?.removeChild(bootEl); } catch {}
    };
    if (rootEl.children.length > 0) {
      removeBoot();
    } else {
      const obs = new MutationObserver(() => {
        if (rootEl.children.length > 0) {
          removeBoot();
          obs.disconnect();
        }
      });
      obs.observe(rootEl, { childList: true });
    }
  }
} catch {}

// -------------------------------------------------------------------
// Pre-mount host guard for platform-only marketing pages.
// -------------------------------------------------------------------
// `/pricing`, `/signup` and `/roadmap` belong to nexuscart.co — on any
// tenant host the merchant chrome (banner/header/footer) wraps the page
// and looks like it's coming FROM that merchant, which is confusing and
// damages brand consistency. `/roadmap` in particular is the SHARED
// Nexus Cart product roadmap (merchants suggest, the platform builds) —
// it is platform-owned, not per-tenant, so it must never render under a
// merchant's storefront. Doing the redirect BEFORE React mounts means
// there is zero flash of the wrong layout. Keep the path list small
// and exact. Mirrors `lib/platform-host.ts` for in-app SPA navigation.
const __isPlatformRedirect = (() => {
  try {
    const PLATFORM_PATHS = new Set(["/pricing", "/signup", "/roadmap"]);
    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    if (!PLATFORM_PATHS.has(path)) return false;
    const host = window.location.hostname.toLowerCase();
    const isPlatform =
      host === "nexuscart.co" ||
      host === "www.nexuscart.co" ||
      host === "app.nexuscart.co" ||
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".replit.dev") ||
      host.endsWith(".replit.app") ||
      host.endsWith(".repl.co");
    if (isPlatform) return false;
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    window.location.replace(`https://nexuscart.co${path}${search}${hash}`);
    return true;
  } catch { return false; }
})();

if (!__isPlatformRedirect) {
  createRoot(document.getElementById("root")!).render(<App />);
}

try { initTracking(); } catch {}

// -------------------------------------------------------------------
// Web Vitals — Core Web Vitals reporting (LCP, CLS, INP, FCP, TTFB)
// -------------------------------------------------------------------
// We use the browser-native PerformanceObserver instead of the
// `web-vitals` npm package to keep the bundle lean (the package is
// ~3KB but adds another HTTP request and a build dep). The metrics
// posted here let us correlate slow-page complaints with actual
// user-experienced LCP/CLS/INP in the API logs.
(() => {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;
  const base = import.meta.env.BASE_URL || "/";
  const ENDPOINT = `${base}api/web-vitals`;
  const queue: Array<{ name: string; value: number; rating?: string; path: string }> = [];
  let flushTimer: any = null;

  const flush = () => {
    if (queue.length === 0) return;
    const payload = JSON.stringify({ metrics: queue.splice(0) });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      } else {
        void fetch(ENDPOINT, { method: "POST", body: payload, headers: { "Content-Type": "application/json" }, keepalive: true });
      }
    } catch { /* swallow — vitals are observability, never break the app */ }
  };

  const enqueue = (name: string, value: number, rating?: string) => {
    queue.push({ name, value: Math.round(value * 100) / 100, rating, path: window.location.pathname });
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, 2000);
  };

  const safeObserve = (type: string, cb: (entries: PerformanceEntryList) => void) => {
    try {
      const po = new PerformanceObserver((list) => cb(list.getEntries()));
      po.observe({ type, buffered: true } as any);
    } catch { /* unsupported metric in this browser — skip */ }
  };

  // LCP — Largest Contentful Paint
  let lcpValue = 0;
  safeObserve("largest-contentful-paint", (entries) => {
    const last = entries[entries.length - 1] as any;
    if (last) lcpValue = last.renderTime || last.loadTime || last.startTime;
  });
  // FCP — First Contentful Paint
  safeObserve("paint", (entries) => {
    for (const e of entries) {
      if (e.name === "first-contentful-paint") enqueue("FCP", e.startTime, e.startTime < 1800 ? "good" : e.startTime < 3000 ? "needs-improvement" : "poor");
    }
  });
  // CLS — Cumulative Layout Shift
  let clsValue = 0;
  safeObserve("layout-shift", (entries) => {
    for (const e of entries as any[]) {
      if (!e.hadRecentInput) clsValue += e.value;
    }
  });
  // INP — Interaction to Next Paint (estimated via event timing)
  let maxInp = 0;
  safeObserve("event", (entries) => {
    for (const e of entries as any[]) {
      if (e.duration > maxInp) maxInp = e.duration;
    }
  });
  // TTFB — Time to First Byte
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    if (nav) enqueue("TTFB", nav.responseStart, nav.responseStart < 800 ? "good" : nav.responseStart < 1800 ? "needs-improvement" : "poor");
  } catch {}

  // Flush LCP/CLS/INP at page unload — they're cumulative until then.
  const finalize = () => {
    if (lcpValue) enqueue("LCP", lcpValue, lcpValue < 2500 ? "good" : lcpValue < 4000 ? "needs-improvement" : "poor");
    enqueue("CLS", clsValue, clsValue < 0.1 ? "good" : clsValue < 0.25 ? "needs-improvement" : "poor");
    if (maxInp) enqueue("INP", maxInp, maxInp < 200 ? "good" : maxInp < 500 ? "needs-improvement" : "poor");
    flush();
  };
  document.addEventListener("visibilitychange", () => { if (document.hidden) finalize(); });
  window.addEventListener("pagehide", finalize);
})();

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).catch(() => {});
  });
}

// -------------------------------------------------------------------
// Auto-reload when a new deployment ships.
// -------------------------------------------------------------------
// When the server boots a new revision, `/api/version` returns a new
// token. Long-lived tabs poll the endpoint and transparently refresh
// themselves — the merchant sees live changes without having to press
// reload. We also catch the common "stale chunk" failure mode: a tab
// that pre-deploy loaded index.html referring to hash `abc.js`, then
// post-deploy lazy-imports that chunk which no longer exists, and
// React hangs on Suspense forever (the "جار التحميل" symptom). A
// single reload pulls the new HTML/chunk map and unfreezes the page.
(() => {
  const base = import.meta.env.BASE_URL || "/";
  const VERSION_URL = `${base}api/version`;
  const RELOAD_FLAG = "__nc_reloaded_at";
  const RELOAD_COOLDOWN_MS = 30_000;

  const canReload = () => {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
      return !last || Date.now() - last > RELOAD_COOLDOWN_MS;
    } catch { return true; }
  };
  const doReload = (reason: string) => {
    if (!canReload()) return;
    try { sessionStorage.setItem(RELOAD_FLAG, String(Date.now())); } catch {}
    try { console.info(`[nexus-cart] reloading: ${reason}`); } catch {}
    // Bypass HTTP cache so we actually pull the fresh HTML + chunk map.
    try { (window.location as any).reload(true); } catch { window.location.reload(); }
  };

  // Chunk / dynamic-import load failures → the only sensible recovery
  // is a hard reload. Gate on the message so we don't reload the page
  // for every unrelated runtime error.
  const isChunkError = (msg: string) =>
    /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg);

  window.addEventListener("error", (e) => {
    const msg = String((e as ErrorEvent).message || "");
    if (isChunkError(msg)) doReload("chunk-load-error");
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason: any = (e as PromiseRejectionEvent).reason;
    const msg = String(reason?.message || reason || "");
    if (isChunkError(msg)) doReload("chunk-load-rejection");
  });

  // Poll for new deployments while the tab stays open.
  let initialVersion: string | null = null;
  const checkVersion = async () => {
    if (document.hidden) return;
    try {
      const r = await fetch(VERSION_URL, { cache: "no-store", credentials: "same-origin" });
      if (!r.ok) return;
      const { version } = await r.json();
      if (!version) return;
      if (initialVersion == null) { initialVersion = String(version); return; }
      if (String(version) !== initialVersion) doReload("version-changed");
    } catch { /* offline / transient — try again next tick */ }
  };
  // Prime immediately, then poll every 60s and on focus.
  void checkVersion();
  setInterval(() => { void checkVersion(); }, 60_000);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) void checkVersion(); });
})();
